import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchVersionsForNewsletter } from '@/store/slices/versionsSlice';
import { fetchActionItems } from '@/store/slices/actionItemsSlice';
import { fetchNewsletters } from '@/store/slices/newslettersSlice';
import VersionHistoryView from '@/components/version/VersionHistory';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/Common';
import { ROUTES } from '@/constants/routes';

export default function VersionHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: versions, status, error } = useAppSelector((state) => state.versions);
  const actionItems = useAppSelector((state) => state.actionItems.items);
  const newsletter = useAppSelector((state) => state.newsletters.items.find((n) => n.id === id));

  useEffect(() => {
    if (!id) return;
    dispatch(fetchVersionsForNewsletter(id));
    dispatch(fetchActionItems());
    dispatch(fetchNewsletters());
  }, [dispatch, id]);

  const actionItemsById = useMemo(() => new Map(actionItems.map((item) => [item.id, item])), [actionItems]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button startIcon={<ArrowBackIcon />} onClick={() => (id ? navigate(ROUTES.newsletterEdit(id)) : navigate(ROUTES.newsletterList))}>
          Back to Editor
        </Button>
      </Stack>
      <Typography variant="h1">Version History</Typography>
      {newsletter && (
        <Typography color="text.secondary" sx={{ mt: -2 }}>
          {newsletter.title}
        </Typography>
      )}

      {status === 'loading' || status === 'idle' ? (
        <LoadingState label="Loading version history…" />
      ) : status === 'failed' ? (
        <ErrorState message={error ?? 'Failed to load versions'} onRetry={() => id && dispatch(fetchVersionsForNewsletter(id))} />
      ) : versions.length === 0 ? (
        <EmptyState
          title="No versions yet"
          description="Publish this newsletter or use “Save Version” in the editor to start tracking history."
        />
      ) : (
        <VersionHistoryView versions={versions} actionItemsById={actionItemsById} />
      )}
    </Stack>
  );
}
